import React, { type FC, useMemo } from 'react'

import styled from '@emotion/styled'
import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue } from 'recoil'

import {
  BUTTON_VARIANT_PRIMARY,
  BUTTON_VARIANT_SECONDARY,
  Button,
  Flex,
  FormElement,
  MESSAGE_BOX_VARIANT_WARNING,
  MessageBox,
  ModalBody,
  ModalFoot,
  Select,
} from 'mailup-web-components'

import {
  modalBulkFindReplaceCancelCallback,
  modalBulkFindReplaceChangeEmptyFieldCallback,
  modalBulkFindReplaceEmptyConfirmCallback,
} from 'Pages/RecipientsList/recoil/callbacks/modalBulkFindReplace'
import { dynamicFieldsState } from 'Pages/RecipientsList/recoil/state/columns'
import { modalBulkFindReplaceState } from 'Pages/RecipientsList/recoil/state/modalBulkFindReplace'
import { selectionCountState } from 'Pages/RecipientsList/recoil/state/results'

const StyledSelect = styled(Select)`
  .e-select__tray {
    max-height: 185px;
  }
`

const TabEmptyField: FC = () => {
  const intl = useIntl()

  const { EmptyField, isEmptyFieldDirty } = useRecoilValue(
    modalBulkFindReplaceState,
  )

  const dynamicFields = useRecoilValue(dynamicFieldsState)

  const totalItems = useRecoilValue(selectionCountState)

  const handleChange = useRecoilCallback(
    modalBulkFindReplaceChangeEmptyFieldCallback,
    [],
  )
  const handleCancel = useRecoilCallback(modalBulkFindReplaceCancelCallback, [])
  const handleConfirm = useRecoilCallback(
    modalBulkFindReplaceEmptyConfirmCallback,
    [],
  )

  const canEmpty = EmptyField !== undefined

  const options = useMemo(
    () =>
      dynamicFields.map((c) => ({
        value: `Field${c.id}`,
        label: c.description,
      })),
    [dynamicFields],
  )

  const selectedField = useMemo(
    () =>
      dynamicFields.find((f) => `Field${f.id}` === EmptyField)?.description ??
      undefined,
    [dynamicFields, EmptyField],
  )

  const feedback =
    isEmptyFieldDirty && EmptyField === undefined
      ? intl.formatMessage({
          id: 'Load.MailupCommon.Required',
        })
      : undefined

  return (
    <>
      <ModalBody scrollable scrollableHeight="500px">
        <Flex
          flexDirection="column"
          gap="24px"
          margin="24px 0 0 0"
          height="251px"
        >
          <FormElement
            label={intl.formatMessage({
              id: 'Load.RecipientsList.EmptyFieldLabel',
            })}
            feedback={feedback}
            feedbackVariant={feedback !== undefined ? 'error' : undefined}
          >
            <StyledSelect
              data-mu-test="find-replace-input-find-column"
              value={EmptyField}
              options={options}
              onChange={handleChange}
              placeholder={intl.formatMessage({
                id: 'Load.RecipientsList.EmptyFieldSelectPlaceholder',
              })}
              error={feedback !== undefined}
            />
          </FormElement>

          {EmptyField !== undefined && (
            <MessageBox variant={MESSAGE_BOX_VARIANT_WARNING}>
              {intl.formatMessage(
                { id: 'Load.RecipientsList.EmptyFieldWarning' },
                {
                  linebreak: <br />,
                  FIELD: selectedField,
                  N: totalItems,
                },
              )}
            </MessageBox>
          )}
        </Flex>
      </ModalBody>

      <ModalFoot>
        <Button
          kind={BUTTON_VARIANT_SECONDARY}
          data-mu-test="cancel"
          onClick={handleCancel}
        >
          {intl.formatMessage({ id: 'Load.MailupCommon.Cancel' })}
        </Button>

        <Button
          kind={BUTTON_VARIANT_PRIMARY}
          data-mu-test="confirm"
          onClick={handleConfirm}
          disabled={!canEmpty}
        >
          {intl.formatMessage(
            {
              id: 'Load.RecipientsList.ActionEmptyConfirm',
            },
            { N: totalItems },
          )}
        </Button>
      </ModalFoot>
    </>
  )
}

export default TabEmptyField
